import { create, type EventEmitter } from 'event-emitter';
import type { CacheEntry } from './entries/cacheEntry';
import type { ChangeContext } from '../client/changeManager';
import type {
  CollectEvent,
  InitEvent,
  PullEvent,
  StateEvent,
} from './resourceLifecycleManager';
import type { DevtoolsCallbacks } from '../devtools/maybeLoadDevtools';

export type NodeEvents = {
  state: StateEvent;
  init: InitEvent;
  invalidation: { changeContext: ChangeContext };
  collect: CollectEvent;
  pull: PullEvent;
};

export type NodeEmitter = EventEmitter<NodeEvents>;

export const createNodeStore = ({
  devtools,
}: {
  devtools?: DevtoolsCallbacks;
}) => {
  const nodes = new Map<string, CacheEntry<any, any>>();
  const emitters = new Map<string, NodeEmitter>();

  const getEmitter = (instanceKey: string): NodeEmitter => {
    const emitter = emitters.get(instanceKey);
    if (!emitter) {
      throw new Error(`No emitter for ${instanceKey}`);
    }
    return emitter;
  };

  return {
    nodes,

    get: (instanceKey: string) => nodes.get(instanceKey),

    getEmitter,

    create: (instanceKey: string, entry: CacheEntry<any, any>) => {
      nodes.set(instanceKey, entry);

      const emitter = create<NodeEvents>();

      emitters.set(instanceKey, emitter);

      return emitter;
    },

    set: (instanceKey: string, entry: CacheEntry<any, any>) => {
      nodes.set(instanceKey, entry);
      devtools?.onStateChange(instanceKey, entry);
    },

    patch: (instanceKey: string, entry: CacheEntry<any, any>) => {
      nodes.set(instanceKey, entry);
    },

    remove: (instanceKey: string) => {
      nodes.delete(instanceKey);
      emitters.delete(instanceKey);
    },
  };
};

export type NodeStore = ReturnType<typeof createNodeStore>;
